import { Swiper } from "swiper";
import { EffectCoverflow, EffectFlip, EffectCards } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-flip";
import "swiper/css/effect-cards";

/**
 * Initialise card collection functionality when DOM is loaded
 */
document.addEventListener("DOMContentLoaded", function () {
	const cardTypeButtons = document.querySelectorAll(".card-type-btn");
	const modal = document.getElementById("cardModal");
	const modalClose = document.querySelector(".modal-close");

	if (!cardTypeButtons.length) return;

	let swiper = null;
	let modalSwiper = null;
	let centerSlideIndex = 0;

	/**
   * Get Swiper configuration for mobile devices
   * @param {number} slideCount - Number of visible slides
   * @returns {Object} Swiper configuration object
   */
	const getMobileConfig = (slideCount) => {
		if (slideCount < 3) {
			return {
				grabCursor: true,
				centeredSlides: true,
				slidesPerView: 1,
				on: {
					slideChange() {
						centerSlideIndex = this.activeIndex;
					},
				},
			};
		}

		return {
			modules: [EffectCards],
			effect: "cards",
			grabCursor: true,
			centeredSlides: true,
			cardsEffect: {
				slideShadows: true,
				transformEl: null,
			},
			on: {
				slideChange() {
					centerSlideIndex = this.activeIndex;
				},
			},
		};
	};

	/**
   * Get Swiper configuration for desktop devices
   * @param {number} slideCount - Number of visible slides
   * @returns {Object} Swiper configuration object
   */
	const getDesktopConfig = (slideCount) => {
		return {
			modules: [EffectCoverflow],
			effect: "coverflow",
			grabCursor: true,
			centeredSlides: true,
			slidesPerView: 3,
			loop: false,
			initialSlide: 2,
			coverflowEffect: {
				depth: 300,
				rotate: 25,
				stretch: 10,
				modifier: 1.2,
				slideShadows: false,
			},
			on: {
				slideChange() {
					centerSlideIndex = this.activeIndex;
				},
			},
		};
	};

	/**
   * Initialise or reinitialise the Swiper instance
   */
	function initSwiper() {
		if (swiper) {
			swiper.destroy(true, true);
		}

		const actualSlides = document.querySelectorAll(".battle-card-slide");
		const slideCount = actualSlides.length;
		const isMobile = window.innerWidth < 992;
		const config = isMobile
			? getMobileConfig(slideCount)
			: getDesktopConfig(slideCount);

		swiper = new Swiper(".card-swiper", config);
		centerSlideIndex = swiper.activeIndex;

		// Add direct click listeners to each slide for better detection
		actualSlides.forEach((slide, index) => {
			slide.addEventListener("click", () => {
				if (index === swiper.activeIndex) {
					openModal(slide);
				} else {
					swiper.slideTo(index);
				}
			});
		});
	}

	/**
   * Open the card modal with front and back images
   * @param {HTMLElement} slide - The clicked slide element
   */
	function openModal(slide) {
		const { featuredImage, cardTitle } = slide.dataset;
		const cardFrontUrl = slide.querySelector(".battle-card-image").src;

		if (!featuredImage || !modal) return;

		const frontImage = modal.querySelector(".modal-front-image");
		const backImage = modal.querySelector(".modal-back-image");

		if (frontImage && backImage) {
			Object.assign(frontImage, { src: cardFrontUrl, alt: cardTitle });
			Object.assign(backImage, { src: featuredImage, alt: cardTitle });
		}

		modal.classList.add("active");
		document.body.style.overflow = "hidden";

		// Initialise Swiper immediately for better mobile experience
		initModalSwiper();

		// Flip automatically to show the back side with a slight delay
		const flipDelay = window.innerWidth < 768 ? 150 : 50;
		setTimeout(() => {
			if (modalSwiper) {
				modalSwiper.slideTo(1);
			}
		}, flipDelay);
	}

	/**
   * Initialise the modal Swiper for card flipping
   */
	function initModalSwiper() {
		if (!modalSwiper) {
			modalSwiper = new Swiper(".modal-swiper", {
				modules: [EffectFlip],
				effect: "flip",
				grabCursor: true,
				flipEffect: { slideShadows: false },
				allowTouchMove: true,
				on: {
					click() {
						this.slideNext();
					},
				},
			});

			// Update Swiper for both mobile and desktop, with delay only for mobile
			if (window.innerWidth < 768) {
				setTimeout(() => {
					modalSwiper.update();
				}, 100);
			} else {
				modalSwiper.update();
			}
		} else {
			modalSwiper.slideTo(0);
		}
	}

	/**
   * Close the card modal and reset state
   */
	function closeModal() {
		if (!modal) return;

		modal.classList.remove("active");
		document.body.style.overflow = "";
		modalSwiper?.slideTo(0);
	}

	let originalSlidesHtml = "";

	/**
   * Filter cards by type and reinitialise Swiper
   * @param {string} selectedType - The card type to filter by ('all' for no filter)
   */
	function filterCards(selectedType) {
		const swiperWrapper = document.querySelector(
			".card-swiper .swiper-wrapper",
		);

		if (!originalSlidesHtml) {
			if (swiper) {
				swiper.destroy(true, true);
				swiper = null;
			}
			originalSlidesHtml = swiperWrapper.innerHTML;
		}

		if (swiper) {
			swiper.destroy(true, true);
			swiper = null;
		}

		swiperWrapper.innerHTML = originalSlidesHtml;

		const allSlides = Array.from(
			swiperWrapper.querySelectorAll(".battle-card-slide"),
		);

		allSlides.forEach((slide) => {
			const cardTypes = slide.dataset.cardTypes.split(",");
			const shouldShow =
				selectedType === "all" || cardTypes.includes(selectedType);

			if (!shouldShow) {
				slide.remove();
			}
		});

		requestAnimationFrame(() => {
			initSwiper();
		});
	}

	/**
   * Handle filter button click events
   * @param {HTMLElement} button - The clicked filter button
   */
	const handleButtonClick = (button) => {
		cardTypeButtons.forEach((btn) => btn.classList.remove("active"));
		button.classList.add("active");
		filterCards(button.dataset.type);
	};

	/**
   * Handle modal background click to close modal
   * @param {Event} e - The click event
   */
	const handleModalClick = (e) => {
		if (e.target === modal) closeModal();
	};

	/**
   * Handle keyboard events for modal
   * @param {KeyboardEvent} e - The keyboard event
   */
	const handleKeydown = (e) => {
		if (e.key === "Escape" && modal?.classList.contains("active")) {
			closeModal();
		}
	};

	cardTypeButtons.forEach((button) => {
		button.addEventListener("click", () => handleButtonClick(button));
	});

	modalClose?.addEventListener("click", closeModal);
	modal?.addEventListener("click", handleModalClick);
	document.addEventListener("keydown", handleKeydown);

	initSwiper();
});
